// ─── Agent Tool Data Models ──────────────────────────────────────────────────
//
// The API/tool definitions of a product's live-call agent — what the agent's
// function-calling tools invoke (the live-call analog of Headline Management's
// API Management rows). Each tool names an endpoint (method + base_url), an
// optional flat payload template (`config`), an optional universal auth entry,
// and the AGENTS (prompts) that use it — one tool can be used by many agents,
// exactly like deck categories. `agents` empty = used by NO agent.
//
// Env: rows are created visible only in the authoring deployment's env
// (`forEnv`, see for-env.util.ts) and are converted to production by PATCHing
// `for_env: 3`. The `baseUrl` field is labeled "Endpoint" in the UI.

/** A prompt (agent) that uses a tool — same shape as the deck's agent refs. */
export interface AgentToolAgentRef {
  id: number;
  name: string;
  isActive: boolean;
}

/** Admin-shape tool returned by the tool endpoints (full record; no preview trimming). */
export interface AgentTool {
  id: number;
  toolName: string;
  /** Stable machine key (snake_case). Immutable after create. */
  toolKeyname: string;
  method: string;
  baseUrl: string;
  /** Payload template: flat key→value map, or null. */
  config: Record<string, string> | null;
  description: string | null;
  /** Universal auth entry the call authenticates with, or null. */
  authId: number | null;
  authName: string | null;
  /** The agent prompts that use this tool. Empty = used by NO agent. */
  agents: AgentToolAgentRef[];
  isActive: boolean;
  /** Row visibility: 1 production, 2 test, 3 both (see for-env.util.ts). */
  forEnv: number;
  productKey?: string;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface AgentToolListResponse {
  /** Echoes the product scope the backend resolved. */
  product: string;
  count: number;
  tools: AgentTool[];
}

/** Body for POST — create one tool (stamped with the deployment's env). */
export interface CreateAgentToolPayload {
  tool_name: string;
  tool_keyname: string;
  method?: string;
  base_url: string;
  config?: Record<string, string> | null;
  description?: string | null;
  auth_id?: number | null;
  is_active?: boolean;
  /** Assign which prompts use the tool. Omit for none. */
  agent_ids?: number[];
}

/** Body for PATCH — all optional. `agent_ids` REPLACES the assignment set ([] clears). */
export interface PatchAgentToolPayload {
  tool_name?: string;
  method?: string;
  base_url?: string;
  /** Null clears the payload template. */
  config?: Record<string, string> | null;
  description?: string | null;
  /** Null clears the auth link. */
  auth_id?: number | null;
  is_active?: boolean;
  /** 1 production / 2 test / 3 both — 3 converts a test tool to production. */
  for_env?: number;
  agent_ids?: number[];
}

/** HTTP verb choices for the form dropdown (mirrors the backend whitelist). */
export const TOOL_METHOD_OPTIONS: ReadonlyArray<string> = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];
